// src/components/webinar_funnel/sections/CampaignTable.jsx
import React, { useState } from 'react';
import {
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import ExpandedRowDetails from './ExpandedRowDetails';
import {
  useHeatmap,
  getHeatmapColor,
  HeatmapLegend,
} from '../../../../utils/heatMapUtils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getPercentageValue = (row, columnIndex) => {
  if (!row.conversions) return 0;
  const value = [
    row.conversions.inviteToReg,
    row.conversions.regToAttend,
    row.conversions.attendToBook,
  ][columnIndex] ?? 0;
  const n = typeof value === 'string' ? parseFloat(value) : (value || 0);
  return isNaN(n) ? 0 : n;
};

// ─── Component ────────────────────────────────────────────────────────────────

const CampaignTable = ({
  data,
  filteredData,
  heatmapEnabled,
  onHeatmapToggle,
  scalingType,
  onScalingTypeChange,
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const displayData = filteredData || data || [];

  const heatmapFields = [
    'conversions.inviteToReg',
    'conversions.regToAttend',
    'conversions.attendToBook',
  ];

  const processedData = React.useMemo(() =>
    displayData.map((row) => ({
      ...row,
      conversions: {
        ...row.conversions,
        inviteToReg:  getPercentageValue(row, 0),
        regToAttend:  getPercentageValue(row, 1),
        attendToBook: getPercentageValue(row, 2),
      },
    })), [displayData]);

  const heatmap = useHeatmap(processedData, heatmapFields, scalingType);

  // Sync heatmap enabled state
  React.useEffect(() => {
    if (heatmapEnabled !== undefined && heatmap.enabled !== heatmapEnabled) {
      heatmap.setEnabled(heatmapEnabled);
    }
  }, [heatmapEnabled, heatmap]);

  // Sync scaling type
  React.useEffect(() => {
    if (scalingType !== undefined && heatmap.scalingType !== scalingType) {
      heatmap.setScalingType(scalingType);
    }
  }, [scalingType, heatmap]);

  // Notify parent of heatmap state changes
  React.useEffect(() => {
    if (typeof onHeatmapToggle === 'function') onHeatmapToggle(heatmap.enabled);
  }, [heatmap.enabled, onHeatmapToggle]);

  React.useEffect(() => {
    if (typeof onScalingTypeChange === 'function') onScalingTypeChange(heatmap.scalingType);
  }, [heatmap.scalingType, onScalingTypeChange]);

  const toggleRow = (index) => {
    const next = new Set(expandedRows);
    next.has(index) ? next.delete(index) : next.add(index);
    setExpandedRows(next);
  };

  const getHeatmapStyle = (value, columnIndex) => {
    if (!heatmap.enabled) return { backgroundColor: 'transparent', color: 'inherit' };
    return {
      ...getHeatmapColor(value, columnIndex, heatmap.scalingInfo, {
        useWhiteText: false,
        textColor: '#152451',
      }),
    };
  };

  if (!displayData.length) {
    return (
      <div className="p-8 text-center text-sm text-grey-400">
        No campaign data available
      </div>
    );
  }

  // Shared th classes
  const thMain = 'px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wide';
  const thSub  = 'px-3 py-2 text-center text-xs font-medium text-white uppercase tracking-wide opacity-90';
  const tdBase = 'px-4 py-2.5 text-center text-sm text-darkblue-500 whitespace-nowrap';

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-grey-200">

        {/* ── Header ────────────────────────────────────────────────────── */}
        <thead style={{ background: 'linear-gradient(135deg, #152451, #1e3a6e)' }}>
          <tr>
            <th className={`${thMain} text-left`} rowSpan={2}>Campaign</th>
            <th className={thMain} rowSpan={2}>Date</th>
            <th className={thMain} rowSpan={2}>Invitations</th>
            <th className={`${thMain} border-l-2 border-darkblue-400`} colSpan={2}>Registration</th>
            <th className={`${thMain} border-l-2 border-darkblue-400`} colSpan={2}>Attendance</th>
            <th className={`${thMain} border-l-2 border-darkblue-400`} colSpan={2}>Booking</th>
          </tr>
          <tr>
            {[
              'Total', 'Conversion %',
              'Total', 'Conversion %',
              'Total', 'Conversion %',
            ].map((label, i) => (
              <th
                key={i}
                className={`${thSub} ${[0, 2, 4].includes(i) ? 'border-l-2 border-darkblue-400' : ''}`}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <tbody className="divide-y divide-grey-100">
          {displayData.map((row, index) => (
            <React.Fragment key={row.id || index}>
              <tr
                className={`cursor-pointer transition-colors ${
                  index % 2 === 0 ? 'bg-white hover:bg-grey-50' : 'bg-grey-50 hover:bg-grey-100'
                }`}
                onClick={() => toggleRow(index)}
              >
                {/* Campaign */}
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1.5">
                    <ChevronRightIcon className={`w-3.5 h-3.5 text-grey-400 flex-shrink-0 transition-transform duration-200 ${expandedRows.has(index) ? 'rotate-90' : ''}`} />
                    <span className="text-xs font-semibold text-darkblue-500">
                      {row.campaign_name || `Campaign ${row.id}`}
                    </span>
                  </div>
                </td>

                {/* Date */}
                <td className={tdBase}>
                  {new Date(row.date).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                  })}
                </td>

                {/* Invitations */}
                <td className={tdBase}>{(row.invitations || 0).toLocaleString()}</td>

                {/* Registrations total */}
                <td className={tdBase}>{(row.registrations?.total || 0).toLocaleString()}</td>

                {/* Registration % — heatmap */}
                <td className={`${tdBase}`} style={getHeatmapStyle(getPercentageValue(row, 0), 0)}>
                  {row.conversions?.inviteToReg || '0.0'}%
                </td>

                {/* Attendees total */}
                <td className={tdBase}>{(row.attendees?.total || 0).toLocaleString()}</td>

                {/* Attendance % — heatmap */}
                <td className={tdBase} style={getHeatmapStyle(getPercentageValue(row, 1), 1)}>
                  {row.conversions?.regToAttend || '0.0'}%
                </td>

                {/* Bookings total */}
                <td className={tdBase}>{(row.bookings?.total || 0).toLocaleString()}</td>

                {/* Booking % — heatmap */}
                <td className={tdBase} style={getHeatmapStyle(getPercentageValue(row, 2), 2)}>
                  {row.conversions?.attendToBook || '0.0'}%
                </td>
              </tr>

              {expandedRows.has(index) && <ExpandedRowDetails row={row} />}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {/* ── Heatmap legend ─────────────────────────────────────────────── */}
      {heatmap.enabled && heatmap.scalingInfo && (
        <div className="px-6 py-4 bg-grey-50 border-t border-grey-200">
          <div className="flex justify-end mb-2">
            <span className="text-xs text-grey-400">
              Based on {displayData.length} row{displayData.length !== 1 ? 's' : ''}
              {filteredData !== undefined && filteredData.length !== (data?.length || 0) && (
                <span className="ml-1">(filtered)</span>
              )}
            </span>
          </div>
          <HeatmapLegend
            scalingInfo={heatmap.scalingInfo}
            fields={['Registration %', 'Attendance %', 'Booking %']}
          />
        </div>
      )}
    </div>
  );
};

export default CampaignTable;